import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAdminGetSupplyBeamLocationsMutation,
  useAdminToggleSupplyBeamLocationStatusMutation,
} from "../../../features/admin/supplyBeamLocations/supplyBeamLocationsApi";
import { MdLocationOn } from "react-icons/md";
import { FiPlus, FiEdit2, FiArrowLeft } from "react-icons/fi";
import { exportToCSV } from "../../../utils/exportCsv";

const initialFilters = {
  location: "",
  state: "",
  district: "",
  city: "",
  limit: 10,
  offset: 0,
};

const TenantLocationManager = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const token = sessionStorage.getItem("access_token");

  // Tenant info passed via navigate state
  const tenantInfo = routeLocation?.state ?? null;

  // Guard: redirect if no tenant selected
  useEffect(() => {
    if (!tenantInfo?.tenant_id) {
      toast.error("No tenant selected. Please select a client first.");
      navigate(-1);
    }
  }, []);

  const [filters, setFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const [
    fetchLocations,
    {
      data: locationsData,
      isLoading: locationsLoading,
      error: locationsError,
    },
  ] = useAdminGetSupplyBeamLocationsMutation();

  const [
    toggleStatus,
    { data: toggleData, error: toggleError },
  ] = useAdminToggleSupplyBeamLocationStatusMutation();

  const loadLocations = (overrideFilters) => {
    if (!tenantInfo?.tenant_id) return;
    const body = overrideFilters ?? filters;
    fetchLocations({
      token,
      tenant_id: tenantInfo.tenant_id,
      body,
    });
  };

  useEffect(() => {
    loadLocations();
  }, []);

  useMemo(() => {
    if (locationsData) {
      setTableData(locationsData.body?.data ?? []);
      setTotalRows(locationsData.body?.total ?? 0);
    }
    if (locationsError) {
      toast.error("Failed to load locations", { id: "locationsErr" });
    }
  }, [locationsData, locationsError]);

  useMemo(() => {
    if (toggleData) {
      toast.success(toggleData.message ?? "Status updated", {
        id: "toggleOk",
      });
      loadLocations();
    }
    if (toggleError) {
      toast.error(toggleError?.data?.message ?? "Failed to update status", {
        id: "toggleErr",
      });
    }
  }, [toggleData, toggleError]);

  const handleSearch = () => {
    const f = { ...filters, offset: 0 };
    setFilters(f);
    setCurrentPage(1);
    loadLocations(f);
  };

  const handleClear = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    loadLocations(initialFilters);
  };

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * perPage;
    const f = { ...filters, offset: newOffset, limit: perPage };
    setFilters(f);
    setCurrentPage(page);
    loadLocations(f);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const newOffset = (page - 1) * newPerPage;
    const f = { ...filters, limit: newPerPage, offset: newOffset };
    setFilters(f);
    setPerPage(newPerPage);
    setCurrentPage(page);
    loadLocations(f);
  };

  const confirmToggle = (row) => {
    const newStatus = row.status === "1" ? "0" : "1";
    const action = newStatus === "1" ? "activate" : "deactivate";
    toast(
      (t) => (
        <span>
          <h6>
            {action === "activate" ? "Activate" : "Deactivate"} this location?
          </h6>
          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                toast.dismiss(t.id);
                toggleStatus({
                  token,
                  tenant_id: tenantInfo.tenant_id,
                  id: row.id,
                  status: newStatus,
                });
              }}
            >
              Yes
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "confirmToggle" }
    );
  };

  const columns = [
    {
      name: "S.No",
      selector: (row, idx) => (currentPage - 1) * perPage + idx + 1,
      width: "65px",
    },
    {
      name: "Location Name",
      selector: (row) => row.location,
      sortable: true,
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{row.location}</div>
          {row.short_name && (
            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
              {row.short_name}
              {row.location_code ? ` · ${row.location_code}` : ""}
            </div>
          )}
        </div>
      ),
      minWidth: "160px",
    },
    {
      name: "Type",
      selector: (row) => row.location_type ?? "—",
      width: "120px",
    },
    {
      name: "Address",
      selector: (row) => row.address,
      wrap: true,
      minWidth: "140px",
    },
    {
      name: "City",
      selector: (row) => row.city,
      width: "90px",
    },
    {
      name: "District",
      selector: (row) => row.district,
      width: "110px",
    },
    {
      name: "State",
      selector: (row) => row.state,
      width: "100px",
    },
    {
      name: "Geofence",
      selector: (row) =>
        row.geofence_per_location != null
          ? `${row.geofence_per_location} m`
          : "—",
      width: "100px",
    },
    {
      name: "Capacity",
      selector: (row) =>
        row.capacity_tracking != null ? row.capacity_tracking : "—",
      width: "90px",
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          style={{
            padding: "3px 10px",
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            background: row.status === "1" ? "#f0fdf4" : "#fef2f2",
            color: row.status === "1" ? "#16a34a" : "#dc2626",
            border: `1.5px solid ${row.status === "1" ? "#bbf7d0" : "#fecaca"}`,
            whiteSpace: "nowrap",
          }}
        >
          {row.status === "1" ? "Active" : "Inactive"}
        </span>
      ),
      width: "90px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() =>
              navigate("/supplyBeamLocations/edit", {
                state: { ...row, tenantInfo },
              })
            }
          >
            <FiEdit2 size={12} className="me-1" />
            Edit
          </button>
          <button
            className={`btn btn-sm ${
              row.status === "1" ? "btn-outline-danger" : "btn-outline-success"
            }`}
            onClick={() => confirmToggle(row)}
          >
            {row.status === "1" ? "Deactivate" : "Activate"}
          </button>
        </div>
      ),
      width: "205px",
      ignoreRowClick: true,
    },
  ];

  const handleExportCSV = () => {
    if (!tableData || tableData.length === 0) {
      toast.error("No locations available to export");
      return;
    }
    const exportColumns = [
      { name: "Location Name", selector: (r) => r.location_name || r.name || "-" },
      { name: "State", selector: (r) => r.state_name || r.state || "-" },
      { name: "District", selector: (r) => r.district_name || r.district || "-" },
      { name: "City", selector: (r) => r.city_name || r.city || "-" },
      { name: "Pincode", selector: (r) => r.pincode || "-" },
      { name: "Address", selector: (r) => r.address || "-" },
      { name: "Status", selector: (r) => r.status === "1" ? "Active" : "Inactive" },
      { name: "Created At", selector: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "-" },
    ];
    exportToCSV(tableData, exportColumns, `${tenantInfo?.slug || 'tenant'}_locations.csv`);
    toast.success("CSV Downloaded Successfully!");
  };

  if (!tenantInfo?.tenant_id) return null;

  return (
    <div className="page-content">
      <div className="card p-4">
        {/* Breadcrumb / Back */}
        <div className="mb-3">
          <button
            className="btn btn-link p-0 text-decoration-none text-muted d-flex align-items-center gap-1"
            style={{ fontSize: 13 }}
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={14} />
            All Clients
          </button>
        </div>

        {/* Header */}
        <div className="borderbtm d-flex justify-content-between align-items-center flex-wrap grid-margin mb-3">
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdLocationOn size={22} color="#fff" />
            </div>
            <div>
              <h4 className="mb-0 card-title">{tenantInfo.name}</h4>
              <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                Supply Beam Locations · Tenant ID: {tenantInfo.tenant_id} ·{" "}
                {tenantInfo.slug}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
            <button
              className="btn btn-success d-flex align-items-center gap-1"
              onClick={handleExportCSV}
            >
              Export CSV
            </button>
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-1"
              onClick={() =>
                navigate("/supplyBeamLocations/manage", { state: tenantInfo })
              }
            >
              Pipeline Blueprint
            </button>
            <button
              className="btn btn-primary d-flex align-items-center gap-1"
              onClick={() =>
                navigate("/supplyBeamLocations/add", { state: { tenantInfo } })
              }
            >
              <FiPlus size={15} />
              Add Location
            </button>
          </div>
        </div>

        {/* Filters */}
        <div
          className="row mb-3 p-3 rounded"
          style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
        >
          {[
            { field: "location", label: "Location Name", placeholder: "Search..." },
            { field: "state", label: "State", placeholder: "State..." },
            { field: "district", label: "District", placeholder: "District..." },
            { field: "city", label: "City", placeholder: "City..." },
          ].map(({ field, label, placeholder }) => (
            <div className="col-md-2 col-sm-6 mb-2" key={field}>
              <label
                className="form-label"
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                {label}
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={placeholder}
                value={filters[field]}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, [field]: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          ))}
          <div className="col-md-2 col-sm-6 mb-2 d-flex align-items-end gap-2">
            <button className="btn btn-primary btn-sm" onClick={handleSearch}>
              Search
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="row">
          <div className="table-responsive">
            <DataTable
              title={`${totalRows} Location${totalRows !== 1 ? "s" : ""}`}
              data={tableData}
              columns={columns}
              progressPending={locationsLoading}
              progressComponent={
                <div className="p-4 text-center text-muted">
                  Loading locations...
                </div>
              }
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              striped
              highlightOnHover
              noDataComponent={
                <div className="p-4 text-center text-muted">
                  No locations found for this tenant. Click "Add Location" to
                  create one.
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantLocationManager;
